<script>
import { authMethods } from '@state/helpers'
import appConfig from '@src/app.config'
import empresaService from '@/src/services/empresa_sistema'

export default {
  page: {
    title: 'Log in',
    meta: [{ name: 'description', content: `Log in to ${appConfig.title}` }],
  },
  data() {
    return {
      user: '',
      password: '',
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
    }
  },
  mounted() {
    document.body.style.paddingBottom = '0'
  },
  methods: {
    ...authMethods,
    getEmpresaId(user) {
      if (!user) return null

      const candidates = [
        user.id_empresa_sistema,
        user.id_empresa,
        user.idEmpresa,
        user.empresa_id,
        user.idEmpresaSistema,
      ]

      for (let i = 0; i < candidates.length; i += 1) {
        const parsed = Number(candidates[i])
        if (!Number.isNaN(parsed) && parsed > 0) {
          return parsed
        }
      }

      return null
    },
    async resolveEmpresaByApi(token) {
      try {
        const response = await empresaService.listarEmpresa(token)
        const empresa =
          response && response.data && response.data.empresa && response.data.empresa[0]
            ? response.data.empresa[0]
            : null
        return empresa || null
      } catch (error) {
        return null
      }
    },
    resolveEmpresaId(empresa) {
      return empresa ? Number(empresa.id_empresa_sistema) : null
    },
    textHasIntegrationMarker(value) {
      return String(value || '')
        .toLowerCase()
        .includes('sankhya')
    },
    isIntegrationCompany(user, empresa) {
      if (!user && !empresa) return false

      const booleanFlags = [
        user && user.empresa_integracao,
        user && user.integration_company,
        user && user.isIntegrationCompany,
        empresa && empresa.empresa_integracao,
        empresa && empresa.integration_company,
        empresa && empresa.isIntegrationCompany,
      ]

      if (booleanFlags.some((item) => item === true || item === 'true' || item === 1)) {
        return true
      }

      const typeFields = [
        user && user.tipo_usuario,
        user && user.tipo_empresa,
        user && user.perfil,
        user && user.role,
        user && user.integracao,
        user && user.integration,
        empresa && empresa.tipo_empresa,
        empresa && empresa.tipo_integracao,
        empresa && empresa.integracao,
        empresa && empresa.integration,
      ]

      if (typeFields.some((item) => String(item || '').toLowerCase().includes('integr'))) {
        return true
      }

      const nameFields = [
        user && user.user,
        user && user.login,
        user && user.username,
        user && user.nome_empresa,
        user && user.empresa,
        empresa && empresa.nome,
        empresa && empresa.nome_fantasia,
        empresa && empresa.razao_social,
        empresa && empresa.login,
      ]

      return nameFields.some((item) => this.textHasIntegrationMarker(item))
    },
    tryToLogIn() {
      this.tryingToLogIn = true
      this.authError = null

      this.logIn({
        usuario: this.user,
        password: this.password,
      })
        .then(async (loggedUser) => {
          this.tryingToLogIn = false
          this.isAuthError = false

          let empresaId = this.getEmpresaId(loggedUser)
          let empresa = null

          if (!empresaId || !this.isIntegrationCompany(loggedUser, null)) {
            empresa = await this.resolveEmpresaByApi(loggedUser && loggedUser.token)
          }

          if (!empresaId) {
            empresaId = this.resolveEmpresaId(empresa)
          }

          if (empresaId === 169) {
            window.localStorage.setItem('modo_demo_169', '1')
          } else {
            window.localStorage.removeItem('modo_demo_169')
          }

          if (this.isIntegrationCompany(loggedUser, empresa)) {
            window.localStorage.setItem('modo_integracao_empresa', '1')
            this.$router.push({ name: 'Painel de Integração' })
            return
          }

          window.localStorage.removeItem('modo_integracao_empresa')
          this.$router.push(this.$route.query.redirectFrom || { name: 'Dashboard' })
        })
        .catch(() => {
          this.tryingToLogIn = false
          this.authError = 'Usuário ou Senha inválidos!'
          this.isAuthError = true
          setTimeout(() => {
            this.isAuthError = false
          }, 3000)
        })
    },
  },
}
</script>

<template>
  <div class="login-page">
    <main class="login-main">
      <section class="login-card">
        <div class="login-logo">
          <img src="@assets/images/logo_dark.png" alt="webreceita" />
        </div>

        <div class="login-head">
          <h2>Acesse sua conta</h2>
          <p>Entre com suas credenciais para continuar.</p>
        </div>

        <b-alert v-model="isAuthError" variant="danger" class="login-alert">{{ authError }}</b-alert>

        <form class="login-form" @submit.prevent="tryToLogIn">
          <div class="field-wrap">
            <label for="login-user">Usuário</label>
            <input id="login-user" v-model="user" class="field-input" type="text" required autocomplete="username" />
          </div>

          <div class="field-wrap">
            <label for="login-password">Senha</label>
            <input
              id="login-password"
              v-model="password"
              class="field-input"
              type="password"
              required
              autocomplete="current-password"
            />
          </div>

          <button type="submit" class="submit-btn" :disabled="tryingToLogIn">
            <span v-if="!tryingToLogIn">Entrar na plataforma</span>
            <b-spinner v-else small></b-spinner>
          </button>
        </form>
      </section>
    </main>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap');

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 80% 20%, rgba(24, 135, 84, 0.12), transparent 32%),
    radial-gradient(circle at 15% 80%, rgba(17, 64, 92, 0.12), transparent 35%),
    #f3f7fa;
  font-family: 'Sora', sans-serif;
}

.login-main {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.login-card {
  width: 100%;
  max-width: 470px;
  border: 1px solid #d3e0ea;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 22px 40px rgba(9, 33, 55, 0.14);
  padding: 30px 28px;
}

.login-logo img {
  width: 220px;
  max-width: 100%;
}

.login-head {
  margin-top: 18px;
  margin-bottom: 14px;
}

.login-head h2 {
  font-size: 25px;
  color: #11384f;
  margin-bottom: 6px;
  font-weight: 700;
}

.login-head p {
  color: #60788b;
  font-size: 13px;
  margin: 0;
}

.login-alert {
  margin-bottom: 12px;
}

.login-form {
  display: grid;
  gap: 12px;
}

.field-wrap label {
  display: block;
  margin-bottom: 6px;
  color: #345870;
  font-size: 12px;
  font-weight: 600;
}

.field-input {
  width: 100%;
  height: 44px;
  border: 1px solid #c9d9e5;
  border-radius: 12px;
  padding: 0 12px;
  font-size: 14px;
  color: #11384f;
  background: #fbfdff;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-input:focus {
  border-color: #1b8a5b;
  box-shadow: 0 0 0 0.2rem rgba(27, 138, 91, 0.15);
}

.submit-btn {
  margin-top: 6px;
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  background: linear-gradient(135deg, #18895a 0%, #0f5f7f 100%);
  box-shadow: 0 12px 22px rgba(11, 53, 76, 0.22);
  transition: transform 0.2s ease, filter 0.2s ease;
}

.submit-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.submit-btn:disabled {
  opacity: 0.7;
}

@media (max-width: 1024px) {
  .login-main {
    padding: 18px;
  }
}
</style>
